<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import FormFooter from './FormFooter.vue';

const emit = defineEmits(['sendMessage', 'cancel']);

const { t } = useI18n();

const body = ref('');
const footer = ref('');

const cardButtonTypeOptions = computed(() => [
  {
    value: 'REPLY',
    label: t('CONVERSATION.EVOLUTION_INTERACTIVE.BUTTON.BUTTON_TYPE_REPLY'),
  },
  {
    value: 'URL',
    label: t('CONVERSATION.EVOLUTION_INTERACTIVE.BUTTON.BUTTON_TYPE_URL'),
  },
  {
    value: 'CALL',
    label: t('CONVERSATION.EVOLUTION_INTERACTIVE.BUTTON.BUTTON_TYPE_CALL'),
  },
  {
    value: 'COPY',
    label: t('CONVERSATION.EVOLUTION_INTERACTIVE.BUTTON.BUTTON_TYPE_COPY'),
  },
]);

const newCardButton = () => ({ type: 'REPLY', displayText: '', value: '' });
const newCard = () => ({
  imageUrl: '',
  videoUrl: '',
  title: '',
  subtitle: '',
  bodyText: '',
  footer: '',
  buttons: [newCardButton()],
});
const cards = ref([newCard()]);

const addCard = () => cards.value.push(newCard());
const removeCard = index => {
  if (cards.value.length <= 1) return;
  cards.value.splice(index, 1);
};
const addCardButton = card => {
  if (card.buttons.length >= 2) return;
  card.buttons.push(newCardButton());
};
const removeCardButton = (card, index) => {
  if (card.buttons.length <= 1) return;
  card.buttons.splice(index, 1);
};

const isValid = computed(
  () =>
    cards.value.length > 0 &&
    cards.value.every(
      card =>
        card.bodyText.trim() && (card.imageUrl.trim() || card.videoUrl.trim())
    )
);

const serializeCardButton = button => {
  const base = { type: button.type, displayText: button.displayText };
  if (button.type === 'COPY') return { ...base, copyCode: button.value };
  return { ...base, id: button.value };
};

const serializeCard = card => ({
  header: {
    imageUrl: card.imageUrl.trim() || undefined,
    videoUrl: card.imageUrl.trim()
      ? undefined
      : card.videoUrl.trim() || undefined,
    title: card.title.trim() || undefined,
    subtitle: card.subtitle.trim() || undefined,
  },
  body: { text: card.bodyText.trim() },
  footer: card.footer.trim() || undefined,
  buttons: card.buttons
    .filter(button => button.displayText.trim())
    .map(serializeCardButton),
});

const submit = () => {
  if (!isValid.value) return;

  emit('sendMessage', {
    message: '',
    contentType: 'carousel',
    contentAttributes: {
      data: {
        body: body.value.trim() || undefined,
        footer: footer.value.trim() || undefined,
        cards: cards.value.map(serializeCard),
      },
    },
  });
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <TextArea
      v-model="body"
      :label="$t('CONVERSATION.EVOLUTION_INTERACTIVE.CAROUSEL.BODY')"
    />
    <Input
      v-model="footer"
      :label="$t('CONVERSATION.EVOLUTION_INTERACTIVE.CAROUSEL.FOOTER')"
    />
    <div class="flex flex-col gap-3">
      <label class="text-sm font-medium text-n-slate-11">
        {{ $t('CONVERSATION.EVOLUTION_INTERACTIVE.CAROUSEL.CARDS') }}
      </label>
      <div
        v-for="(card, cardIndex) in cards"
        :key="cardIndex"
        class="flex flex-col gap-2 p-3 rounded-lg outline outline-1 outline-n-weak"
      >
        <div class="flex items-center justify-end">
          <NextButton
            v-if="cards.length > 1"
            icon="i-lucide-x"
            ghost
            slate
            sm
            type="button"
            @click="removeCard(cardIndex)"
          />
        </div>
        <Input
          v-model="card.imageUrl"
          :label="
            $t('CONVERSATION.EVOLUTION_INTERACTIVE.CAROUSEL.CARD_IMAGE_URL')
          "
        />
        <Input
          v-if="!card.imageUrl.trim()"
          v-model="card.videoUrl"
          :label="
            $t('CONVERSATION.EVOLUTION_INTERACTIVE.CAROUSEL.CARD_VIDEO_URL')
          "
        />
        <Input
          v-model="card.title"
          :label="$t('CONVERSATION.EVOLUTION_INTERACTIVE.CAROUSEL.CARD_TITLE')"
        />
        <Input
          v-model="card.subtitle"
          :label="
            $t('CONVERSATION.EVOLUTION_INTERACTIVE.CAROUSEL.CARD_SUBTITLE')
          "
        />
        <TextArea
          v-model="card.bodyText"
          :label="$t('CONVERSATION.EVOLUTION_INTERACTIVE.CAROUSEL.CARD_BODY')"
        />
        <Input
          v-model="card.footer"
          :label="$t('CONVERSATION.EVOLUTION_INTERACTIVE.CAROUSEL.CARD_FOOTER')"
        />
        <div class="flex flex-col gap-2">
          <label class="text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION.EVOLUTION_INTERACTIVE.CAROUSEL.CARD_BUTTONS') }}
          </label>
          <div
            v-for="(button, buttonIndex) in card.buttons"
            :key="buttonIndex"
            class="flex items-center gap-2"
          >
            <Select
              v-model="button.type"
              :options="cardButtonTypeOptions"
              class="shrink-0"
            />
            <Input
              v-model="button.displayText"
              class="grow"
              :placeholder="
                $t('CONVERSATION.EVOLUTION_INTERACTIVE.BUTTON.BUTTON_LABEL')
              "
            />
            <Input v-model="button.value" class="grow" />
            <NextButton
              v-if="card.buttons.length > 1"
              icon="i-lucide-x"
              ghost
              slate
              sm
              type="button"
              @click="removeCardButton(card, buttonIndex)"
            />
          </div>
          <NextButton
            v-if="card.buttons.length < 2"
            icon="i-lucide-plus"
            faded
            slate
            sm
            type="button"
            class="self-start"
            :label="$t('CONVERSATION.EVOLUTION_INTERACTIVE.BUTTON.ADD_BUTTON')"
            @click="addCardButton(card)"
          />
        </div>
      </div>
      <NextButton
        icon="i-lucide-plus"
        faded
        slate
        sm
        type="button"
        class="self-start"
        :label="$t('CONVERSATION.EVOLUTION_INTERACTIVE.CAROUSEL.ADD_CARD')"
        @click="addCard"
      />
    </div>
    <FormFooter
      :disabled="!isValid"
      @cancel="$emit('cancel')"
      @submit="submit"
    />
  </div>
</template>
